import { useMemo } from 'react'
import { useAuthStore, type UserRole } from '@/stores/authStore'

/**
 * Permission types for different resources
 */
export type Permission =
  | 'view:dashboard'
  | 'view:conversations'
  | 'view:tasks'
  | 'view:assistants'
  | 'edit:assistants'
  | 'create:assistants'
  | 'delete:assistants'
  | 'view:users'
  | 'edit:users'
  | 'create:users'
  | 'delete:users'
  | 'view:customers'
  | 'edit:customers'
  | 'create:customers'
  | 'delete:customers'
  | 'view:partners'
  | 'edit:partners'
  | 'create:partners'
  | 'delete:partners'
  | 'view:widgets'
  | 'view:prompt-templates'
  | 'edit:prompt-templates'
  | 'view:onboarding'
  | 'view:simulations'
  | 'access:admin-panel'

/**
 * Permission matrix by role
 */
const rolePermissions: Record<UserRole, Permission[]> = {
  admin: [
    'view:dashboard',
    'view:conversations',
    'view:tasks',
    'view:assistants',
    'edit:assistants',
    'create:assistants',
    'delete:assistants',
    'view:users',
    'edit:users',
    'create:users',
    'delete:users',
    'view:customers',
    'edit:customers',
    'create:customers',
    'delete:customers',
    'view:partners',
    'edit:partners',
    'create:partners',
    'delete:partners',
    'view:widgets',
    'view:prompt-templates',
    'edit:prompt-templates',
    'view:onboarding',
    'view:simulations',
    'access:admin-panel',
  ],
  partner: [
    'view:dashboard',
    'view:conversations',
    'view:tasks',
    'view:assistants',
    'edit:assistants',
    'create:assistants',
    'delete:assistants',
    'view:users',
    'edit:users',
    'create:users',
    'view:customers',
    'edit:customers',
    'create:customers',
    'view:partners',
    'view:prompt-templates',
    'view:onboarding',
  ],
  user: [
    'view:dashboard',
    'view:conversations',
    'view:tasks',
    'view:assistants',
    'edit:assistants',
    'view:users',
  ],
  demo: [
    'view:dashboard',
    'view:conversations',
    'view:tasks',
    'view:assistants',
  ],
}

export function canAccessWidgetHub({
  role,
  customerId,
  partnerId,
}: {
  role: string
  customerId?: number
  partnerId?: number
}): boolean {
  return (
    role === 'admin' ||
    (role === 'partner' && partnerId !== undefined) ||
    (role === 'user' && customerId !== undefined)
  )
}

/**
 * Hook for checking user permissions based on their role
 */
export function usePermissions() {
  const user = useAuthStore((state) => state.user)
  const role = user?.role || 'demo'

  const permissions = useMemo(() => {
    const userPermissions = new Set(rolePermissions[role] || [])

    return {
      /**
       * Check if user has a specific permission
       */
      can: (permission: Permission): boolean => {
        return userPermissions.has(permission)
      },

      /**
       * Check if user has all of the specified permissions
       */
      canAll: (permissionList: Permission[]): boolean => {
        return permissionList.every((p) => userPermissions.has(p))
      },

      /**
       * Check if user has any of the specified permissions
       */
      canAny: (permissionList: Permission[]): boolean => {
        return permissionList.some((p) => userPermissions.has(p))
      },

      /**
       * Get all permissions for current role
       */
      permissions: Array.from(userPermissions),

      /**
       * Current user role
       */
      role,

      /**
       * Convenience checks for common role-based access
       */
      isAdmin: role === 'admin',
      isPartner: role === 'partner',
      isUser: role === 'user',
      isDemo: role === 'demo',

      /**
       * Check if user can manage (admin or partner)
       */
      canManage: role === 'admin' || role === 'partner',
    }
  }, [role])

  return permissions
}

export default usePermissions
